import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { UserEndpointClient } from '../../../../repository/UserManagement/Managers/UserServiceClientPb';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, refreshSession, getSession, ACCOUNTANT_API_URL, TRADING_API_URL } from '../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../app/Helpers';
import { TableState } from '../../../common/Table/TableSate';
import { number } from 'yup';
import { GetCategoriesResponse, GetCategoriesRequest, DeleteCategoryRequest, DeleteCategoryResponse } from '../../../../repository/Trading/category_pb';
import { CategoryEndpointClient } from '../../../../repository/Trading/CategoryServiceClientPb';


export interface CategoriesPageState {
    set: TableState
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    searchText: string | undefined,
    filters: any[],
    availabilityFilterOption: { key: number, text: string },
    categories: TableState,

}

const initialState: CategoriesPageState = {
    set: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },
    isChangeStateLoading: false,
    message: undefined,
    searchText: undefined,
    filters: [],
    availabilityFilterOption: { key: 0, text: "" },
    categories: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },

}

const client = new CategoryEndpointClient(TRADING_API_URL, null, null);


export const getItems = createAsyncThunk<GetCategoriesResponse.AsObject, APIRequest<GetCategoriesRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/trading/categories/fetch',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {

                var r = await client.getCategories(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)


export const deleteItem = createAsyncThunk<APIResponse<DeleteCategoryResponse.AsObject>, APIRequest<DeleteCategoryRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/trading/categories/delete',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.delete(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)

export const getParentCategories = createAsyncThunk<GetCategoriesResponse.AsObject, APIRequest<GetCategoriesRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/trading/categories/getParentCategories',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {

                var r = await client.getCategories(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)


const getFilters = (state: CategoriesPageState): any[] => {
    const selected = [];
    if (state.availabilityFilterOption.key != 0) {
        selected.push({ key: 1, name: state.availabilityFilterOption.text });
    }
    if (state.categories.selected.length > 0) {
        selected.push({ key: 2, name: state.categories.selected.at(0).nameAr + " | " + state.categories.selected.at(0).nameEn });
    }
    return selected;
}

export const categoriesPageSlice = createSlice({
    name: 'pages/trading/categories',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.set = initialState.set;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.searchText = initialState.searchText;
            state.availabilityFilterOption = initialState.availabilityFilterOption;
            state.filters = initialState.filters;
            state.categories = initialState.categories;

        },
        resetSet: (state) => {
            state.set.items = [];
            state.set.hasMore = true;
        },
        resetCategories: (state) => {
            state.categories.items = [];
            state.categories.hasMore = true;
        },
        setIsFilteredCategoriesSet: (state, action: PayloadAction<boolean>) => {
            state.categories.isFilteredSet = action.payload;
        },
        setSelectedCategoryItems: (state, action: PayloadAction<any[]>) => {
            state.categories.selected = action.payload;
            state.filters = getFilters(state);
        },

        setSort: (state, action: PayloadAction<boolean>) => {
            state.set.isDescending = action.payload;
        },
        setNumberOfResults: (state, action: PayloadAction<number>) => {
            state.set.numberOfResults = action.payload;
        },
        setSelectedItems: (state, action: PayloadAction<any[]>) => {
            state.set.selected = action.payload;
        },
        setSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.searchText = action.payload;
        },
        setIsFilteredSet: (state, action: PayloadAction<boolean>) => {
            state.set.isFilteredSet = action.payload;
        },
        setAvailabilityFilterOption: (state, action: PayloadAction<{ key: number, text: string }>) => {
            state.availabilityFilterOption = action.payload;
            state.filters = getFilters(state);
        },

        addItem: (state, action: PayloadAction<any>) => {
            if (!state.set.isFilteredSet) {
                if (!state.set.hasMore && !state.set.isDescending) {
                    state.set.items.push(action.payload);
                } else if (state.set.isDescending) {
                    state.set.items.unshift(action.payload);
                }
            }
        },
        updateItem: (state, action: PayloadAction<any>) => {
            let selected = state.set.selected;
            if (action?.payload) {
                if (selected.length > 0 && selected.at(0).id == action?.payload?.id) {
                    selected[0] = action?.payload
                }
                var u = state.set.items.findIndex(e => e.id == action?.payload?.id)
                if (u >= 0) {
                    state.set.items[u] = action?.payload
                }
            }

        }

    },
    extraReducers: (builder) => {
        builder.addCase(getItems.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.categoriesList.map(val => {
                    return {
                        id: val.id,
                        nameAr: val.namear?.value ?? "",
                        nameEn: val.nameen?.value ?? "",
                        isAvailable: val.isavailable?.value ?? undefined,
                        rank: val.rank?.value,
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.createdby?.firstname?.value ?? "") + " " + (val.createdby?.lastname?.value ?? ""),
                        addedById: val.createdby?.id?.value,
                        parentCategoryId: val?.parent?.id?.value,
                        parentCategoryNameAr: val?.parent?.namear?.value ?? "",
                        parentCategoryNameEn: val?.parent?.nameen?.value ?? "",
                        iconSource: val?.iconsource?.value,
                    }
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                let l = [];
                if (state.set.items.length != 0) {
                    l = state.set.items.slice(0, state.set.items.length - 10)
                }
                l = l.concat(r);
                if (r.length < state.set.numberOfResults) {
                    state.set.hasMore = false;
                } else {
                    l = l.concat(...Array(10).fill(null));
                }

                state.set.items = l
                //console.log(state.items)

            } else {
                state.set.hasMore = false;
            }
            state.set.isFetching = false

        })
        builder.addCase(getItems.rejected, (state, action) => {
            state.set.isFetching = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(getItems.pending, (state, action) => {
            state.set.isFetching = true;
            state.message = undefined;
        })


        builder.addCase(getParentCategories.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.categoriesList.map(val => {
                    return {
                        id: val.id,
                        nameAr: val.namear?.value ?? "",
                        nameEn: val.nameen?.value ?? "",
                        isAvailable: val.isavailable?.value ?? undefined,
                        rank: val.rank?.value,
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.createdby?.firstname?.value ?? "") + " " + (val.createdby?.lastname?.value ?? ""),
                        addedById: val.createdby?.id?.value,
                        parentCategoryId: val?.parent?.id?.value,
                        parentCategoryNameAr: val?.parent?.namear?.value ?? "",
                        parentCategoryNameEn: val?.parent?.nameen?.value ?? "",
                        iconSource: val?.iconsource?.value,
                    }
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                if (r.length < state.categories.numberOfResults) {
                    state.categories.hasMore = false;
                }
                state.categories.items = state.categories.items.concat(r);
                //console.log(state.items)

            } else {
                state.categories.hasMore = false;
            }
            state.categories.isFetching = false

        })
        builder.addCase(getParentCategories.rejected, (state, action) => {
            state.categories.isFetching = false;
            if (action.payload) {
                //state.message = action.payload;
            }
        })
        builder.addCase(getParentCategories.pending, (state, action) => {
            state.categories.isFetching = true;
            //state.message = undefined;
        })

        builder.addCase(deleteItem.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
                let selected = state.set.selected;
                if (payload?.metadata) {
                    if (selected.length > 0 && selected.at(0).id == payload?.metadata.id) {
                        state.set.selected = []
                    }
                    var u = state.set.items.findIndex(e => e.id == payload?.metadata.id)
                    if (u >= 0) {
                        state.set.items.splice(u, 1);
                    }
                }
            }
        })
        builder.addCase(deleteItem.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(deleteItem.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })




    }
})

export const { reset, dismissMessage, setSort, resetCategories, setIsFilteredCategoriesSet, setSelectedCategoryItems, setNumberOfResults, resetSet, setSelectedItems, setSearchText, setIsFilteredSet, addItem, updateItem, setAvailabilityFilterOption } = categoriesPageSlice.actions

export default categoriesPageSlice.reducer


